// ─── Feuille : propriétés personnalisées de l'inventaire ──────────
// Reprise fidèle de MoPCal (renderSheetProps + renderSheetPropForm) : chaque
// inventaire porte une liste de propriétés (texte / nombre / date / liste de
// choix) qui deviennent des champs saisissables sur chaque article
// (FeuilleElement les rend depuis `proprietes` dans `vals`).
// Conventions app : liste avec swipe ← supprimer / → éditer, 💾 grisée tant
// que rien à enregistrer, une seule Modal (le formulaire est un calque interne).
import React, { useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet } from 'react-native';
import { useUI, useT, Feuille, LigneSwipe, confirmerSuppression } from '@mop/core';
import { TYPES_PROP, labelTypeProp, nouvelId } from './services/inventaires';

export default function FeuilleProprietes({ visible, proprietes = [], onEnregistrer, onFermer }) {
  const UI = useUI();
  const t = useT();
  const labelType = (id) => labelTypeProp(id, t);
  const s = useMemo(() => makeStyles(UI), [UI]);
  const [form, setForm] = useState(null);   // { id|null, nom, type, options[] , nomInit, typeInit, optInit }

  const fermer = () => { setForm(null); onFermer && onFermer(); };
  const nouveau = () => setForm({ id: null, nom: '', type: 'texte', options: [], nomInit: '', typeInit: 'texte', optInit: '' });
  const editer = (p) => setForm({
    id: p.id, nom: p.nom || '', type: p.type || 'texte', options: p.options || [],
    nomInit: p.nom || '', typeInit: p.type || 'texte', optInit: (p.options || []).join('\n'),
  });

  const optTexte = form ? (form.options || []).join('\n') : '';
  const setOptTexte = (v) => setForm(f => ({ ...f, options: v.split('\n').map(x => x.trim()).filter(Boolean) }));

  const formModifie = !!form && !!form.nom.trim() && (
    !form.id || form.nom.trim() !== form.nomInit || form.type !== form.typeInit
    || (form.type === 'liste' && (form.options || []).join('\n') !== form.optInit)
  );

  function enregistrerForm() {
    if (!form || !formModifie) return;
    const p = {
      id: form.id || nouvelId('p'),
      nom: form.nom.trim(),
      type: form.type,
      ...(form.type === 'liste' ? { options: form.options || [] } : {}),
    };
    const liste = form.id
      ? proprietes.map(x => (x.id === form.id ? p : x))
      : [...proprietes, p];
    onEnregistrer && onEnregistrer(liste);
    setForm(null);
  }
  function supprimer(p) {
    confirmerSuppression(t('inventaire.supprProprieteTitre'), t('inventaire.supprProprieteMsg', { nom: p.nom }),
      () => onEnregistrer && onEnregistrer(proprietes.filter(x => x.id !== p.id)));
  }

  const enForm = !!form;
  return (
    <Feuille
      visible={visible}
      titre={enForm ? (form.id ? t('inventaire.modifierPropriete') : t('inventaire.nouvellePropriete')) : t('inventaire.proprietesPersoTitre')}
      emoji="🏷️"
      onFermer={enForm ? undefined : fermer}
      action={enForm
        ? { icone: form.id ? '💾' : '＋', onPress: enregistrerForm, disabled: !formModifie, label: t('commun.enregistrer') }
        : { icone: '＋', onPress: nouveau, label: t('inventaire.nouvellePropriete') }}
    >
      {enForm ? (
        <>
          {/* Retour à la liste depuis le formulaire (pas de 2e Modal). */}
          <TouchableOpacity style={s.retour} onPress={() => setForm(null)}>
            <Text style={s.retourTxt}>‹ {t('inventaire.proprietes')}</Text>
          </TouchableOpacity>
          <Text style={s.lbl}>{t('listes.nom')}</Text>
          <TextInput style={s.input} value={form.nom} autoFocus={!form.id}
            onChangeText={(v) => setForm(f => ({ ...f, nom: v }))}
            placeholder={t('inventaire.phProprieteExemple')} placeholderTextColor={UI.faint} />
          <Text style={s.lbl}>{t('historique.dType')}</Text>
          <View style={s.typeRow}>
            {TYPES_PROP.map(ty => (
              <TouchableOpacity key={ty.id} style={[s.typeChip, form.type === ty.id && s.typeChipOn]}
                onPress={() => setForm(f => ({ ...f, type: ty.id }))}>
                <Text style={[s.typeChipTxt, form.type === ty.id && s.typeChipTxtOn]}>{labelTypeProp(ty.id, t)}</Text>
              </TouchableOpacity>
            ))}
          </View>
          {form.type === 'liste' && (
            <>
              <Text style={s.lbl}>{t('inventaire.optionsParLigne')}</Text>
              <TextInput style={[s.input, { height: 120, textAlignVertical: 'top' }]} multiline
                value={optTexte} onChangeText={setOptTexte}
                placeholder={t('inventaire.phOptionsListe')} placeholderTextColor={UI.faint} />
            </>
          )}
        </>
      ) : (
        <>
          <Text style={s.intro}>{t('inventaire.proprietesPersoIntro')}</Text>
          {proprietes.length === 0
            ? <Text style={s.vide}>{t('inventaire.aucuneProprietePerso')}</Text>
            : proprietes.map(p => (
              <LigneSwipe key={p.id} espacement={8} onSupprimer={() => supprimer(p)} onEditer={() => editer(p)}>
                <TouchableOpacity style={s.row} activeOpacity={0.7} onPress={() => editer(p)}>
                  <Text style={s.rowNom} numberOfLines={1}>{p.nom}</Text>
                  <Text style={s.rowType}>{labelType(p.type)}</Text>
                </TouchableOpacity>
              </LigneSwipe>
            ))}
        </>
      )}
    </Feuille>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  intro:  { fontSize: 12.5, color: UI.muted, marginBottom: 12 },
  vide:   { fontSize: 13, color: UI.faint, fontStyle: 'italic', marginBottom: 12 },
  row:    { flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: UI.card, borderRadius: 11,
            borderWidth: 1, borderColor: UI.border, padding: 12 },
  rowNom: { flex: 1, fontSize: 14.5, fontWeight: '700', color: UI.ink },
  rowType:{ fontSize: 12, fontWeight: '700', color: UI.muted, borderWidth: 1, borderColor: UI.border,
            borderRadius: 999, paddingHorizontal: 9, paddingVertical: 2 },
  retour:    { alignSelf: 'flex-start', marginBottom: 6 },
  retourTxt: { fontSize: 14, color: UI.accent, fontWeight: '600' },
  lbl:    { fontSize: 12, fontWeight: '700', color: UI.muted, marginTop: 14, marginBottom: 7 },
  input:  { backgroundColor: UI.champ, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 11,
            fontSize: 15, color: UI.ink, borderWidth: 0.5, borderColor: UI.champBord },
  typeRow:  { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  typeChip: { paddingHorizontal: 13, paddingVertical: 8, borderRadius: 999, backgroundColor: UI.champ,
              borderWidth: 0.5, borderColor: UI.champBord },
  typeChipOn:{ backgroundColor: UI.accentTint, borderColor: UI.accent },
  typeChipTxt:  { fontSize: 13, color: UI.ink, fontWeight: '600' },
  typeChipTxtOn:{ color: UI.accent, fontWeight: '800' },
});
